import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Car, Pencil, Trash2, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, DataTable, SearchInput, FilterChips, OverflowMenu, Dialog, Field, FormRow,
  StatusPill, fmtDate, num, plural,
} from '../../components/ui';

// Fleet: company vehicles, their rego and servicing, and how many upcoming shifts use each one.
// The header above already says "Fleet", so the page opens straight on the toolbar.

// The four states a vehicle can be in. Older records were saved as 'active' (and a few with no
// status at all), which the page used to show as "Active" on the card while counting 0 available.
// Both mean "in service and free to book", i.e. Available — one function decides it for the
// badge, the filter counts and the edit form, so the numbers always match the rows.
const STATUS_OPTS = ['available', 'in_use', 'maintenance', 'retired'];
const VEHICLE_STATUS = {
  available:   { label: 'Available',   tone: 'success' },
  in_use:      { label: 'In use',      tone: 'info' },
  maintenance: { label: 'Maintenance', tone: 'warning' },
  retired:     { label: 'Retired',     tone: 'neutral' },
};
const LEGACY_STATUS = { active: 'available', '': 'available' };

function vehicleStatus(v) {
  const raw = String((v && v.status) || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (VEHICLE_STATUS[raw]) return raw;
  if (raw in LEGACY_STATUS) return LEGACY_STATUS[raw];
  return raw; // an unknown code is shown as-is rather than silently re-labelled
}

// Whole calendar days from today to a 'YYYY-MM-DD' date (negative = in the past).
function daysUntil(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

// Rego state: expired (rose), due within 30 days (amber), valid, or not recorded.
function regoState(expiry) {
  const days = daysUntil(expiry);
  if (days == null) return { key: 'unknown', days: null };
  if (days < 0) return { key: 'expired', days };
  if (days <= 30) return { key: 'expiring', days };
  return { key: 'valid', days };
}

const ymd = (v) => (v ? String(v).split('T')[0] : '');
const BLANK = { registration: '', make: '', model: '', color: '', year: '', rego_expiry: '', service_due: '', status: 'available', notes: '' };

function VehicleDialog({ vehicle, onClose, onSaved }) {
  const [form, setForm] = useState(() => (vehicle
    ? { ...BLANK, ...vehicle, year: vehicle.year || '', status: vehicleStatus(vehicle) || 'available',
        rego_expiry: ymd(vehicle.rego_expiry), service_due: ymd(vehicle.service_due), color: vehicle.color || '', notes: vehicle.notes || '',
        make: vehicle.make || '', model: vehicle.model || '' }
    : { ...BLANK }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    if (e) e.preventDefault();
    if (!form.registration.trim()) { setError('Enter the registration plate.'); return; }
    setSaving(true);
    try {
      const payload = {
        registration: form.registration, make: form.make, model: form.model, color: form.color,
        year: form.year ? parseInt(form.year, 10) : null, rego_expiry: form.rego_expiry || null,
        service_due: form.service_due || null, status: form.status, notes: form.notes,
      };
      const { data } = vehicle ? await api.put(`/vehicles/${vehicle.id}`, payload) : await api.post('/vehicles', payload);
      toast.success(vehicle ? 'Vehicle updated' : 'Vehicle added');
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save the vehicle');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} title={vehicle ? `Edit ${vehicle.registration}` : 'Add vehicle'}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="vehicle-form" disabled={saving} className="btn-primary">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {vehicle ? 'Save changes' : 'Add vehicle'}
          </button>
        </>
      )}>
      <form id="vehicle-form" onSubmit={save} className="space-y-3.5">
        <Field label="Registration plate" required error={error}>
          {(id) => (
            <input id={id} className="input-field" value={form.registration} placeholder="ABC123" autoComplete="off"
              aria-invalid={!!error}
              onChange={(e) => { set('registration', e.target.value.toUpperCase()); if (error) setError(''); }} />
          )}
        </Field>
        <FormRow>
          <Field label="Make">{(id) => <input id={id} className="input-field" value={form.make} onChange={(e) => set('make', e.target.value)} placeholder="Toyota" />}</Field>
          <Field label="Model">{(id) => <input id={id} className="input-field" value={form.model} onChange={(e) => set('model', e.target.value)} placeholder="HiAce" />}</Field>
          <Field label="Colour">{(id) => <input id={id} className="input-field" value={form.color} onChange={(e) => set('color', e.target.value)} placeholder="White" />}</Field>
          <Field label="Year">{(id) => <input id={id} type="number" className="input-field" value={form.year} onChange={(e) => set('year', e.target.value)} placeholder="2023" min="1990" max="2030" />}</Field>
          <Field label="Rego expiry">{(id) => <input id={id} type="date" className="input-field" value={form.rego_expiry} onChange={(e) => set('rego_expiry', e.target.value)} />}</Field>
          <Field label="Next service due">{(id) => <input id={id} type="date" className="input-field" value={form.service_due} onChange={(e) => set('service_due', e.target.value)} />}</Field>
        </FormRow>
        <Field label="Status">
          {(id) => (
            <select id={id} className="input-field" value={form.status} onChange={(e) => set('status', e.target.value)}>
              {STATUS_OPTS.map((s) => <option key={s} value={s}>{VEHICLE_STATUS[s].label}</option>)}
              {!VEHICLE_STATUS[form.status] && form.status && <option value={form.status}>{form.status}</option>}
            </select>
          )}
        </Field>
        <Field label="Notes">
          {(id) => <textarea id={id} className="input-field" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Anything the team should know" />}
        </Field>
      </form>
    </Dialog>
  );
}

function RegoCell({ expiry }) {
  const r = regoState(expiry);
  if (r.key === 'unknown') return <span className="text-slate-500">Not recorded</span>;
  return (
    <div className="leading-tight">
      <div className="text-slate-800 tabular-nums">{fmtDate(ymd(expiry))}</div>
      {r.key === 'expired' && <div className="text-[12px] font-medium text-rose-600">Expired {r.days === -1 ? 'yesterday' : `${num(-r.days)} d ago`}</div>}
      {r.key === 'expiring' && <div className="text-[12px] font-medium text-amber-700">{r.days === 0 ? 'Expires today' : `Expires in ${num(r.days)} d`}</div>}
    </div>
  );
}

export default function Vehicles() {
  const confirm = useConfirm();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(null); // null | 'add' | vehicle
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api.get('/vehicles')
      .then((r) => setVehicles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLoadError("We couldn't load the fleet. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stable, so the dialog's focus handling doesn't restart on every render of this page.
  const closeModal = useCallback(() => setModal(null), []);

  const del = async (v) => {
    const shifts = Number(v.upcoming_shifts) || 0;
    const ok = await confirm(
      shifts > 0
        ? `${v.registration} is on ${plural(shifts, 'upcoming shift')}. Those shifts stay booked but will no longer have a vehicle.`
        : `${v.registration} comes off the fleet list. Shifts that used it stay as they are, without a vehicle.`,
      { title: `Remove ${v.registration}?`, confirmLabel: 'Remove vehicle', danger: true },
    );
    if (!ok) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success('Vehicle removed'); load(); }
    catch { toast.error('Could not remove the vehicle'); }
  };

  // Counts come from the same functions that draw each row's badges.
  const counts = useMemo(() => {
    const c = { all: vehicles.length, rego_expiring: 0, rego_expired: 0 };
    STATUS_OPTS.forEach((s) => { c[s] = 0; });
    vehicles.forEach((v) => {
      const s = vehicleStatus(v);
      c[s] = (c[s] || 0) + 1;
      const r = regoState(v.rego_expiry).key;
      if (r === 'expiring') c.rego_expiring += 1;
      if (r === 'expired') c.rego_expired += 1;
    });
    return c;
  }, [vehicles]);

  const matchesFilter = useCallback((v) => {
    if (!filter) return true;
    if (filter === 'rego_expiring') return regoState(v.rego_expiry).key === 'expiring';
    if (filter === 'rego_expired') return regoState(v.rego_expiry).key === 'expired';
    return vehicleStatus(v) === filter;
  }, [filter]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return vehicles.filter((v) => matchesFilter(v)
      && (!s || [v.registration, v.make, v.model, v.color, v.notes].some((f) => String(f || '').toLowerCase().includes(s))));
  }, [vehicles, search, matchesFilter]);

  const chips = [
    { value: null, label: 'All', count: counts.all },
    ...STATUS_OPTS.map((s) => ({ value: s, label: VEHICLE_STATUS[s].label, count: counts[s] || 0 })),
    { value: 'rego_expiring', label: 'Rego due in 30 days', count: counts.rego_expiring, tone: counts.rego_expiring ? 'amber' : undefined },
    { value: 'rego_expired', label: 'Rego expired', count: counts.rego_expired, tone: counts.rego_expired ? 'rose' : undefined },
  ];

  const columns = [
    {
      key: 'registration', header: 'Rego', sortable: true, width: '9rem',
      render: (v) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); setModal(v); }}
          className="font-semibold text-slate-900 hover:text-primary-700 hover:underline underline-offset-2 text-left">
          {v.registration}
        </button>
      ),
    },
    {
      key: 'vehicle', header: 'Vehicle', sortable: true,
      sortValue: (v) => [v.make, v.model].filter(Boolean).join(' ').toLowerCase() || null,
      render: (v) => {
        const name = [v.year, v.make, v.model].filter(Boolean).join(' ');
        return (
          <div className="min-w-[10rem] leading-tight">
            <div className="text-slate-800">{name || <span className="text-slate-500">Not recorded</span>}{v.color ? <span className="text-slate-500"> · {v.color}</span> : null}</div>
            {v.notes && <div className="text-[12px] text-slate-500 truncate max-w-[22rem]" title={v.notes}>{v.notes}</div>}
          </div>
        );
      },
    },
    {
      key: 'status', header: 'Status', sortable: true, sortValue: (v) => vehicleStatus(v),
      render: (v) => <StatusPill status={vehicleStatus(v)} map={VEHICLE_STATUS} />,
    },
    {
      key: 'rego_expiry', header: 'Rego expiry', sortable: true, sortValue: (v) => ymd(v.rego_expiry) || null,
      render: (v) => <RegoCell expiry={v.rego_expiry} />,
    },
    {
      key: 'service_due', header: 'Service due', sortable: true, sortValue: (v) => ymd(v.service_due) || null,
      render: (v) => {
        if (!v.service_due) return <span className="text-slate-500">Not recorded</span>;
        const d = daysUntil(v.service_due);
        return (
          <div className="leading-tight">
            <div className="tabular-nums">{fmtDate(ymd(v.service_due))}</div>
            {d != null && d < 0 && <div className="text-[12px] font-medium text-amber-700">Overdue</div>}
          </div>
        );
      },
    },
    {
      key: 'upcoming_shifts', header: 'Upcoming shifts', align: 'right', sortable: true,
      sortValue: (v) => Number(v.upcoming_shifts) || 0,
      render: (v) => <span className="tabular-nums">{num(Number(v.upcoming_shifts) || 0)}</span>,
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', width: '3.5rem',
      render: (v) => (
        <OverflowMenu label={`Actions for ${v.registration}`} size={16} className="p-2.5" items={[
          { label: 'Edit vehicle', icon: Pencil, onClick: () => setModal(v) },
          { divider: true },
          { label: 'Remove from fleet', icon: Trash2, danger: true, onClick: () => del(v) },
        ]} />
      ),
    },
  ];

  const filtersOn = !!filter || !!search.trim();

  return (
    <div className={PAGE_WIDE}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center min-w-0">
          <SearchInput value={search} onChange={setSearch} placeholder="Search rego, make, model or notes" className="w-full sm:w-72" />
          <p className="text-[13px] text-slate-500 tabular-nums whitespace-nowrap" aria-live="polite">
            {loading ? 'Loading…' : filtersOn ? `${num(filtered.length)} of ${plural(vehicles.length, 'vehicle')}` : plural(vehicles.length, 'vehicle')}
          </p>
        </div>
        <button type="button" onClick={() => setModal('add')} className="btn-primary self-start lg:self-auto">
          <Plus size={16} /> Add vehicle
        </button>
      </div>

      <FilterChips options={chips} value={filter} onChange={setFilter} multi={false} ariaLabel="Filter vehicles" />

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <DataTable
          label="Company vehicles"
          columns={columns}
          rows={filtered}
          loading={loading}
          error={loadError}
          onRetry={load}
          stickyHeader={false}
          defaultSort={{ key: 'registration', dir: 'asc' }}
          onRowClick={(v) => setModal(v)}
          empty={vehicles.length === 0 ? {
            icon: Car,
            title: 'No vehicles yet',
            hint: 'Add your company vehicles to track rego, servicing and which shifts use them.',
            action: <button type="button" onClick={() => setModal('add')} className="btn-primary btn-sm"><Plus size={14} /> Add vehicle</button>,
          } : {
            icon: Car,
            title: 'No vehicles match these filters',
            action: <button type="button" onClick={() => { setFilter(null); setSearch(''); }} className="btn-secondary btn-sm">Clear filters</button>,
          }}
        />
      </div>

      {modal && (
        <VehicleDialog
          vehicle={modal === 'add' ? null : modal}
          onClose={closeModal}
          onSaved={load}
        />
      )}
    </div>
  );
}
